<script setup>
import { computed, onMounted } from 'vue'
import { Icon, ProfilePicture } from '@components'
import { getPrice, addSpaceAfterComma } from '@utils'
import moment from 'moment'

import { useAppointments } from '@store/appointments.js'
const $appointments = useAppointments()

import { useSettings } from '@store/settings.js'
const $settings = useSettings()

import { useCustomizer } from '@store/customizer.js'
const $customizer = useCustomizer()

const findStaffById = (id) => {
    return $appointments.staffs.find((staff) => staff.staff_id == id)
}

const $prebook = computed(() => {
    return window.prebook || {}
})

// format time depending on customizer
const formatTime = (time, format) => {
    if ($settings.customizer.time.show_time) {
        return moment(time, format).format('HH:mm')
    } else {
        return moment(time, format).format('hh:mm A')
    }
}

// const width = () => {
//     let prebookBookingWrapper = document.querySelector('.main-wrapper')
//     console.log(prebookBookingWrapper.clientWidth)
// }

const getServiceById = (id) => {
    return $appointments.services.find((service) => service.service_id == id)
}

const showServicePrice = (price) => {
    let type = getServiceById($appointments.confirmation.service_id)?.price_type
    if (type == 'fixed') {
        return getPrice(price)
    } else if (type == 'free') {
        return 'Free'
    }
}
</script>

<template>
    <div v-if="$appointments.confirmation.meta" id="print-frontend-confirmation" class="confirmation-content-wrapper" style="display: flex;padding-left: 20px;padding-right: 20px;flex-direction: column;gap: 24px;text-align: center;">
        <!-- header -->
        <div class="header-wrapper" style="padding-top: 4px;padding-bottom: 12px; text-align: center;">
            <!-- <img class="header-image" src="@assets/images/shape_14.png" style="margin-bottom: 16px;width: 120px;height: 120px;margin: 0 auto;"> -->
            <div class="header-subtitle-1" style="font-size: 16px;line-height: 24px;font-weight: 500;color: #101828;text-transform: capitalize;">Hey, {{ $prebook.customer.display_name }}, Congratulations</div>
            <div class="header-title" style="margin-top: 8px;color: #101828;font-size: 20px;" :style="$customizer.titleStyle">Your <span class="capitalize">{{ $settings.labels?.appointment?.singular }}</span> is Confirmed!</div>
            <div class="header-subtitle-2" style="margin-top: 16px;font-size: 16px;line-height: 24px;font-weight: 600;color: #667085;"><span class="capitalize">{{ $settings.labels?.appointment?.singular }}</span> ID: #{{ $appointments.confirmation.appointment_id }}</div>
        </div>

        <!-- details -->
        <div class="details-wrapper" style="display: flex;padding-top: 12px;padding-bottom: 12px;padding-left: 16px;padding-right: 16px;flex-direction: column;gap: 12px;border-width: 1px;border-color: #eaecf0;background: #f9fafb;" :style="`border-radius: ${$customizer.isRounded ? '8px' : '0'}`">
            <div class="details-item" style="display: flex;gap: 12px;align-items: center;">
                <Icon name="calendar" size="24px" class="item-Icon" style="color: #667085;"></Icon>
                <div class="item-title" style="font-size: 14px;line-height: 20px;font-weight: 500;color: #101828;">Date</div>
                <div class="item-text" style="font-size: 14px;line-height: 20px;font-weight: 600;color: #101828;margin-left: auto;">{{ moment($appointments.confirmation.booking_date, 'YYYY-MM-DD hh:mm:ss').format('MMMM DD, YYYY') }}</div>
            </div>

            <div class="divider" style="width: 100%;height: 1px;background: #eaecf0;"></div>

            <div class="details-item" style="display: flex;gap: 12px;align-items: center;">
                <Icon name="hash02" size="24px" class="item-Icon" style="color: #667085;"></Icon>
                <div class="item-title" style="font-size: 14px;line-height: 20px;font-weight: 500;color: #101828; text-transform: capitalize;">{{ $settings.labels?.service?.singular }}</div>
                <div class="item-text" style="font-size: 14px;line-height: 20px;font-weight: 600;color: #101828;margin-left: auto;">{{ $appointments.confirmation.meta?.service_name }}</div>
            </div>

            <div v-if="$settings.addons.staffs" class="divider" style="width: 100%;height: 1px;background: #eaecf0;"></div>

            <div v-if="$settings.addons.staffs" class="details-item" style="display: flex;gap: 12px;align-items: center;">
                <Icon name="user01" size="24px" class="item-Icon" style="color: #667085;"></Icon>
                <div class="item-title" style="font-size: 14px;line-height: 20px;font-weight: 500;color: #101828; text-transform: capitalize;">{{ $settings.labels?.staff?.singular }}</div>
                <div class="flex items-center gap-2 item-text justify-end" style="font-size: 14px;line-height: 20px;font-weight: 600;color: #101828;margin-left: auto;width:calc(100% - 82px)">
                    <!-- <div class="item-image" :style="`background-image: url(${findStaffById($appointments.confirmation.staff_id).profile_image_url ? findStaffById($appointments.confirmation.staff_id).profile_image_url : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'});`"></div> -->
                    <ProfilePicture :name="$appointments.confirmation.meta?.staff_name" :img="findStaffById($appointments.confirmation.staff_id)?.profile_image_url" :bgColor="findStaffById($appointments.confirmation.staff_id)?.color" textClass="!w-6 !h-6 !text-base" imgClass="!w-6 !h-6" />
                    {{ $appointments.confirmation.meta?.staff_name }}
                </div>
            </div>

            <div class="divider" style="width: 100%;height: 1px;background: #eaecf0;"></div>

            <div class="details-item" style="display: flex;gap: 12px;align-items: center;">
                <Icon name="clock" size="24px" class="item-Icon" style="color: #667085;"></Icon>
                <div class="item-title" style="font-size: 14px;line-height: 20px;font-weight: 500;color: #101828;">Local Time</div>
                <div class="item-text" style="font-size: 14px;line-height: 20px;font-weight: 600;color: #101828;margin-left: auto;">{{ formatTime($appointments.confirmation.booking_time_start, 'HH:mm') }} - {{ formatTime($appointments.confirmation.booking_time_end, 'HH:mm') }}</div>
            </div>

            <div class="divider" style="width: 100%;height: 1px;background: #eaecf0;" v-if="$appointments.confirmation.meta.address"></div>

            <div class="details-item" style="display: flex;gap: 12px;align-items: center;" v-if="$appointments.confirmation.meta.address">
                <Icon name="markerPin01" size="24px" class="item-Icon" style="color: #667085;"></Icon>
                <div class="item-title" style="font-size: 14px;line-height: 20px;font-weight: 500;color: #101828;">Location</div>
                <div class="item-text text-end" style="font-size: 14px;line-height: 20px;font-weight: 600;color: #101828;margin-left: auto; overflow: hidden;width:calc(100% - 108px)">{{ addSpaceAfterComma($appointments.confirmation.meta?.address) }}</div>
            </div>

            <div class="divider" style="width: 100%;height: 1px;background: #eaecf0;" v-if="getServiceById($appointments.confirmation.service_id)?.price_type == 'fixed'"></div>

            <div class="details-item" style="display: flex;gap: 12px;align-items: center;" v-if="getServiceById($appointments.confirmation.service_id)?.price_type == 'fixed'">
                <Icon name="bankNote01" size="24px" class="item-Icon" style="color: #667085;"></Icon>
                <div class="item-title" style="font-size: 14px;line-height: 20px;font-weight: 500;color: #101828;"><span class="capitalize">{{ $settings.labels?.payment?.singular }}</span> Method</div>
                <div class="item-text" style="font-size: 14px;line-height: 20px;font-weight: 600;color: #101828;margin-left: auto;text-transform: capitalize;">{{ $appointments.confirmation.meta?.payment_method || $prebook.settings.payment.default_payment_method || 'Cash' }}</div>
            </div>
        </div>

        <!-- cost info -->
        <div v-if="getServiceById($appointments.confirmation.service_id)?.price_type != 'none'" class="cost-wrapper" style="display: flex;padding: 12px;flex-direction: column;border-width: 1px;border-color: #eaecf0;" :style="`border-radius: ${$customizer.isRounded ? '8px' : '0'};`">
            <!-- service amount -->
            <div class="cost-item-type-1" style="display: flex;justify-content: space-between;font-size: 14px;line-height: 20px;font-weight: 400;color: #344054;">
                <div>{{ $appointments.confirmation.meta?.service_name }}</div>
                <div>{{ showServicePrice($appointments.confirmation.meta?.price) }}</div>
            </div>

            <!-- extra amount -->
            <!-- <div class="mt-2 cost-item-type-1" style="display: flex;justify-content: space-between;font-size: 14px;line-height: 20px;font-weight: 400;color: #344054;">
                        <div>Extras
                        </div>
                        <div>$10</div>
                    </div> -->

            <!-- subtotal amount -->
            <!-- <div class="mt-3 cost-item-type-2" style="display: flex;justify-content: space-between;font-size: 16px;line-height: 24px;font-weight: 600;color: #101828;">
                        <div>Sub total
                        </div>
                        <div>$110</div>
                    </div> -->

            <!-- tax amount -->
            <!-- <div class="mt-2 cost-item-type-1" style="display: flex;justify-content: space-between;font-size: 14px;line-height: 20px;font-weight: 400;color: #344054;">
                        <div>Salex tax (8%)
                        </div>
                        <div>$8.80</div>
                    </div> -->

            <!-- divider -->
            <div class="my-3 divider" style="width: 100%;height: 1px;background: #eaecf0;"></div>

            <!-- total amount -->
            <div class="cost-item-type-2" style="display: flex;justify-content: space-between;font-size: 16px;line-height: 24px;font-weight: 600;color: #101828;">
                <div>Total</div>
                <div>{{ showServicePrice($appointments.confirmation.meta?.price) }}</div>
            </div>
        </div>

        <!-- cta -->
        <!-- <div class="cta-wrapper" style="padding-top: 2px;padding-bottom: 2px;padding-left: 8px;padding-right: 8px;border-radius: 8px;background: #f2f4f7;">
                    <div class="cta-item" style="display: flex;padding-top: 8px;padding-bottom: 8px;padding-right: 8px;padding-left: 10px;gap: 8px;font-size: 14px;line-height: 20px;">
                        <div class="cta-text" style="font-weight: 400;color: #101828;">Questions?Contact our</div>
                        <a href="https://google.com" class="cta-link" style="font-weight: 600;color: #7f56d9;">Customer Support</a>
                    </div>
                </div> -->
    </div>
</template>
